import { useCallback, useMemo, useRef } from 'react'
import { useLingui } from '@lingui/react/macro'

import DateTimeInput from '@/components/ui/DateTimeInput'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import debounce from 'lodash.debounce'

interface RecDateProps {
  startDate: Date
  duration: number
  showUntil: boolean
  isRecurring: boolean
  onChange: (date: Date, duration: number) => void
}

const HOUR_DURATION_MILLISECONDS = 60 * 60 * 1_000
const DAY_DURATION_MILLISECONDS = 24 * HOUR_DURATION_MILLISECONDS

export function RecDate(props: RecDateProps) {
  const { startDate, duration, showUntil, isRecurring, onChange } = props

  const { t } = useLingui()
  const date = useMemo(() => startDate.toISOString().split('T')[0], [startDate])

  const time = useMemo(
    () => startDate.toISOString().split('T')[1].substring(0, 5),
    [startDate]
  )

  const isWholeDay = useMemo(() => {
    // Check that it actually starts at 0 on the day
    if (startDate.getUTCHours() !== 0) return false
    if (startDate.getUTCMinutes() !== 0) return false
    if (startDate.getUTCSeconds() !== 0) return false
    if (startDate.getUTCMilliseconds() !== 0) return false

    // Check that the duration actually takes the whole day
    if (duration !== DAY_DURATION_MILLISECONDS) return false
    return true
  }, [startDate, duration])

  const endDate = useMemo(() => {
    return new Date(startDate.getTime() + duration)
  }, [startDate, duration])

  const debouncedOnChange = useRef(debounce(onChange, 1500))

  const onEndDateChange = useCallback(
    (newEndDate: Date) => {
      if (!newEndDate) return

      const endTime = newEndDate.getTime()
      const startTime = startDate.getTime()

      const givenDuration = endTime - startTime

      // We do this with debouncing, since it is confusing for the user when he types "13", with first the 1, which would always lead to adding +1 days
      // Normally we would do this with onBlur, but Radix Popovers and blurring aren't the best friends, so this would basically destroy the blurring.
      // There might be a better solution, let's take a look at it again in the future.
      if (startTime > endTime) {
        const laterEndDuration = endTime + DAY_DURATION_MILLISECONDS - startTime

        debouncedOnChange.current(startDate, laterEndDuration)
      } else {
        debouncedOnChange.current.cancel()
      }

      onChange(startDate, givenDuration)
    },
    [startDate, onChange]
  )

  const onChangeWholeDay = useCallback(
    (isEnabled: boolean) => {
      if (isEnabled) {
        const day = new Date(startDate.getTime())
        day.setUTCHours(0, 0, 0, 0)

        onChange(day, DAY_DURATION_MILLISECONDS)
      }

      if (!isEnabled) {
        onChange(startDate, HOUR_DURATION_MILLISECONDS)
      }
    },
    [startDate, onChange]
  )

  const onChangeFullDay = useCallback(
    (date: string) => {
      if (!date) return

      onChange(new Date(date), duration)
    },
    [duration, onChange]
  )

  const onChangeDay = useCallback(
    (day: string) => {
      if (!day) return

      const newDate = new Date(`${day}T${time}Z`)

      onChange(newDate, duration)
    },
    [duration, time, onChange]
  )

  const onChangeTime = useCallback(
    (time: string) => {
      const newTime = time ? time : '00:00'

      const newDate = new Date(`${date}T${newTime}Z`)

      onChange(newDate, duration)
    },
    [duration, date, onChange]
  )

  return (
    <div className="flex flex-col gap-1">
      <Field
        label={t({
          comment:
            'Checkbox label that asks the user if he wants to let a recurrence/availability be shown for the whole day.',
          message: `Whole day?`
        })}
        canBeDisabled
        onEnabledChange={onChangeWholeDay}
        size="small"
        isInitialEnabled={isWholeDay}
      >
        <Input
          type="date"
          className="flex-1"
          value={date}
          onChange={event => onChangeFullDay(event.target.value)}
        />
      </Field>
      {!isWholeDay && (
        <>
          {isRecurring && (
            <>
              <Field label={t`Start on`} size="small">
                <Input
                  value={date}
                  type="date"
                  onChange={event => onChangeDay(event.target.value)}
                />
              </Field>
              <Field label={showUntil ? t`From` : t`At`} size="small">
                <Input
                  type="time"
                  value={time}
                  onChange={event => onChangeTime(event.target.value)}
                />
              </Field>
            </>
          )}
          {!isRecurring && (
            <Field label={showUntil ? t`From` : t`At`} size="small">
              <DateTimeInput
                value={startDate}
                onChange={date => onChange(date, duration)}
              />
            </Field>
          )}
          {showUntil && (
            <Field label={t`Until`} size="small">
              <DateTimeInput
                value={endDate}
                onChange={onEndDateChange}
                isOffset={isRecurring}
                startDate={startDate}
              />
            </Field>
          )}
        </>
      )}
    </div>
  )
}
